import React, { useState, useEffect, useContext } from 'react';
import { toast } from 'react-toastify';
import { authFetch, authPost } from '../../Utilities/Api';
import AuthContext from '../../context/AuthContext';
import { useChatContext } from '../../context/ChatContext';
import './CreateGroupForm.css';

const CreateGroupForm = ({ onClose }) => {
    const { user } = useContext(AuthContext);
    const { fetchChats } = useChatContext();

    const [groupName, setGroupName] = useState('');
    const [selectedMemberIds, setSelectedMemberIds] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [availableFriends, setAvailableFriends] = useState([]);
    const [loading, setLoading] = useState(false);

    // 1. Load Friends
    useEffect(() => {
        const loadFriends = async () => {
            console.log("🔄 [CreateGroup] Fetching friends list...");
            try {
                // 🛑 FIXED: Changed '/api/friends' to '/friends' (Api.js adds /api automatically)
                const res = await authFetch('/friends');
                const data = await res.json();

                console.log("📥 [CreateGroup] Raw API Response:", data);

                let friendsList = [];
                if (Array.isArray(data)) {
                    friendsList = data;
                } else if (data.data && Array.isArray(data.data)) {
                    friendsList = data.data;
                } else if (data.friends && Array.isArray(data.friends)) {
                    friendsList = data.friends;
                }

                console.log(`✅ [CreateGroup] Loaded ${friendsList.length} friends.`);
                setAvailableFriends(friendsList);
            } catch (err) {
                console.error("❌ [CreateGroup] Failed to load friends", err);
            }
        };
        if (user) loadFriends();
    }, [user]);

    // 2. Toggle Selection
    const toggleMember = (friendId) => {
        setSelectedMemberIds(prev =>
            prev.includes(friendId)
                ? prev.filter(id => id !== friendId)
                : [...prev, friendId]
        );
    };

    // 3. Submit Handler
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!groupName.trim()) return toast.error("Group name is required");
        if (selectedMemberIds.length < 1) return toast.error("Select at least 1 friend");

        setLoading(true);

        try {
            const payload = {
                name: groupName.trim(),
                members: selectedMemberIds,
                avatar: ""
            };

            console.log("🚀 [CreateGroup] Submitting Payload:", payload);

            // 🛑 FIXED: Changed '/api/chats/group' to '/chats/group'
            const res = await authPost('/chats/group', payload);
            const data = await res.json();

            if (res.ok) {
                console.log("✅ [CreateGroup] Success:", data);
                toast.success(`Group "${data.name}" created!`);
                if (fetchChats) fetchChats();
                if (onClose) onClose();
            } else {
                console.error("❌ [CreateGroup] API Error:", data);
                toast.error(data.message || "Failed to create group");
            }
        } catch (err) {
            console.error("💥 [CreateGroup] Server Error:", err);
            toast.error("Server error");
        } finally {
            setLoading(false);
        }
    };

    // Filter Logic
    const filteredFriends = availableFriends.filter(item => {
        const friend = item.user || item;
        if (!friend || !friend.username) return false;
        const name = (friend.nickname || friend.username || "").toLowerCase();
        return name.includes(searchTerm.toLowerCase());
    });

    return (
        <div className="create-group-container">
            <h3>Create Group Chat</h3>

            <form onSubmit={handleSubmit}>
                <div className="form-group">
                    <label>Group Name</label>
                    <input
                        type="text"
                        placeholder="e.g. Weekend Plans"
                        value={groupName}
                        onChange={(e) => setGroupName(e.target.value)}
                        autoFocus
                    />
                </div>

                <div className="form-group">
                    <label>Add Members</label>
                    <input
                        type="text"
                        placeholder="Search friends..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="search-input"
                    />
                </div>

                <div className="members-selection-list">
                    {filteredFriends.length > 0 ? (
                        filteredFriends.map(item => {
                            const friend = item.user || item;
                            const isSelected = selectedMemberIds.includes(friend._id);

                            return (
                                <div
                                    key={friend._id}
                                    className={`member-option ${isSelected ? 'selected' : ''}`}
                                    onClick={() => toggleMember(friend._id)}
                                >
                                    <img src={friend.profilePicture || "/default-avatar.png"} alt="avatar" />
                                    <span>{friend.nickname || friend.username}</span>
                                    {isSelected && <span className="check-mark">✔</span>}
                                </div>
                            );
                        })
                    ) : (
                        <p className="no-results">
                            {availableFriends.length === 0
                                ? "No friends found (Try adding some first!)"
                                : "No matching friends found"}
                        </p>
                    )}
                </div>

                <div className="form-actions">
                    <button type="button" className="btn-cancel" onClick={onClose}>Cancel</button>
                    <button type="submit" className="btn-create" disabled={loading}>
                        {loading ? 'Creating...' : `Create (${selectedMemberIds.length})`}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default CreateGroupForm;